import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/_core/hooks/useAuth";
import {
  Eye, EyeOff, Loader2, ArrowRight, Mail, Lock, User,
  KeyRound, ArrowLeft, CheckCircle2, ShieldCheck, RefreshCw
} from "lucide-react";

/**
 * Parse a Convex Auth error into a PT-BR user-facing message.
 *
 * Convex Auth (`signIn`) may throw:
 *   - ConvexError({ code, message })  — preferred shape from our convex/auth.ts
 *   - generic Error with .message     — provider-level failures (Resend, Google, password mismatch)
 *
 * We surface `data.message` when available, otherwise fall back to a sensible default per flow.
 */
function authErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object") {
    // ConvexError shape: err.data.{code,message}
    const data = (err as { data?: { message?: string } }).data;
    if (data?.message) return data.message;
    const message = (err as { message?: string }).message;
    if (message) return message;
  }
  return fallback;
}

type AuthView =
  | "login"
  | "register"
  | "otp-login"
  | "otp-verify"
  | "register-verify"
  | "forgot-password"
  | "reset-password"
  | "reset-success";

export default function Login() {
  const { signInPassword, signInOtp, requestReset, confirmReset, isAuthenticated, loading: authLoading } =
    useAuth();

  // F20b (2026-05-13): redirect pra dashboard quando session fica válida.
  // Convex Auth seta isAuthenticated=true após signIn/confirmReset, mas Login
  // continua montado se ninguém der window.location/setLocation. Wouter route
  // path="/login" não tem AuthGate, então sem este effect o user fica preso.
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      window.location.href = "/";
    }
  }, [authLoading, isAuthenticated]);

  const [view, setView] = useState<AuthView>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // OTP
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const autoSubmitRef = useRef<(() => void) | null>(null);

  // Resend countdown
  const [resendCountdown, setResendCountdown] = useState(0);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startResendCountdown = useCallback(() => {
    setResendCountdown(60);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  // Wire auto-submit to the correct handler based on current view
  const formSubmitRef = useRef<HTMLFormElement | null>(null);
  useEffect(() => {
    if (view === "otp-verify") {
      autoSubmitRef.current = () => formSubmitRef.current?.requestSubmit();
    } else if (view === "register-verify") {
      autoSubmitRef.current = () => formSubmitRef.current?.requestSubmit();
    } else if (view === "reset-password") {
      // Don't auto-submit reset-password (user still needs to type new password)
      autoSubmitRef.current = null;
    } else {
      autoSubmitRef.current = null;
    }
  }, [view]);

  const resetForm = () => {
    setError(null);
    setSuccess(null);
    setOtpDigits(["", "", "", "", "", ""]);
  };

  const otpCode = otpDigits.join("");

  // ── OTP Input Handlers ──
  const updateOtpDigits = (newDigits: string[]) => {
    setOtpDigits(newDigits);
    // Auto-submit when all 6 digits are filled
    if (newDigits.every((d) => d !== "") && autoSubmitRef.current) {
      setTimeout(() => autoSubmitRef.current?.(), 100);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    // Handle paste via onChange (some browsers trigger onChange instead of onPaste)
    if (value.length > 1) {
      const digits = value.replace(/\D/g, "").slice(0, 6);
      if (digits.length > 1) {
        const newDigits = ["", "", "", "", "", ""];
        for (let i = 0; i < Math.min(digits.length, 6); i++) {
          newDigits[i] = digits[i];
        }
        updateOtpDigits(newDigits);
        const focusIdx = Math.min(digits.length, 5);
        otpRefs.current[focusIdx]?.focus();
        return;
      }
    }

    if (!/^\d*$/.test(value)) return;
    const newDigits = [...otpDigits];
    newDigits[index] = value.slice(-1);
    updateOtpDigits(newDigits);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const newDigits = ["", "", "", "", "", ""];
    for (let i = 0; i < Math.min(pasted.length, 6); i++) {
      newDigits[i] = pasted[i];
    }
    updateOtpDigits(newDigits);
    const focusIdx = Math.min(pasted.length, 5);
    otpRefs.current[focusIdx]?.focus();
  };

  // ── Auth Calls (Convex Auth via useAuth helpers) ──
  // signInPassword(email, password, flow): flow = "signIn" (login) | "signUp" (register)
  // signInOtp(email): triggers Resend OTP provider — server emails the 6-digit code
  // The convex/auth.ts setSession callback persists the JWT in the auth cookie automatically,
  // so we no longer touch setAuthToken — just redirect after success.

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signInPassword(email, password, "signIn");
      window.location.href = "/";
    } catch (err) {
      setError(authErrorMessage(err, "Erro ao fazer login"));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // Convex Auth "signUp" flow creates the user immediately and signs them in.
      // The legacy two-step register→OTP-verify is collapsed into a single call;
      // we still surface the "register-verify" view so users that need email
      // verification see the OTP screen via the dedicated `signInOtp` flow below.
      await signInPassword(email, password, "signUp");
      // TODO(F5-followup): if convex/auth.ts enables a separate "email-verification"
      // flow on signUp, branch on the response here instead of redirecting blindly.
      window.location.href = "/";
    } catch (err) {
      setError(authErrorMessage(err, "Erro ao registrar"));
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // Email verification reuses the OTP provider with the code the user typed.
      // The user is already provisioned by `signInPassword(..., "signUp")`, so
      // this call confirms the email and completes the session.
      await signInOtp(email);
      // After OTP succeeds Convex Auth sets the session; redirect home.
      window.location.href = "/";
    } catch (err) {
      setError(authErrorMessage(err, "Código inválido"));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signInOtp(email);
      setSuccess("Código enviado!");
      startResendCountdown();
      setView("otp-verify");
      setOtpDigits(["", "", "", "", "", ""]);
    } catch (err) {
      setError(authErrorMessage(err, "Erro ao enviar código"));
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async (_purpose: "login" | "register" | "reset_password") => {
    // All three purposes route through the same Resend OTP provider — the server
    // disambiguates by email + active flow, so the client just re-triggers signInOtp.
    setError(null);
    setLoading(true);
    try {
      await signInOtp(email);
      setSuccess("Novo código enviado!");
      startResendCountdown();
      setOtpDigits(["", "", "", "", "", ""]);
    } catch (err) {
      setError(authErrorMessage(err, "Erro ao reenviar código"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // For OTP login the verification step is the same `signInOtp` call — the
      // Resend provider auto-completes when it sees a pending challenge for `email`.
      // TODO(F5-followup): if Convex Auth exposes a dedicated `verify` action with
      // the typed code, replace this with that call so the request is idempotent.
      await signInOtp(email);
      window.location.href = "/";
    } catch (err) {
      setError(authErrorMessage(err, "Código inválido"));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // F20 (2026-05-13): usar requestReset (flow "reset") em vez de signInOtp.
      // Convex Auth dispara o ResendOTPReset provider (subject correto + verification
      // type=reset). signInOtp era login standalone — código não servia pra reset.
      await requestReset(email);
      setSuccess("Código enviado!");
      startResendCountdown();
      setView("reset-password");
      setOtpDigits(["", "", "", "", "", ""]);
    } catch (err) {
      setError(authErrorMessage(err, "Erro"));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // F20 (2026-05-13): chama confirmReset (flow "reset-verification") com
      // {email, code, newPassword}. Convex Auth valida OTP, troca hash, cria
      // session — useConvexAuth fica isAuthenticated=true automaticamente e
      // App.tsx redireciona pro dashboard.
      //
      // Antes (BUG): signInPassword(..., "signUp") tentava criar conta nova,
      // ignorava o OTP, criava session efêmera, e useAuth não atualizava →
      // app voltava pro /login após "Redefinir".
      await confirmReset(email, otpCode, newPassword);
      setView("reset-success");
    } catch (err) {
      setError(authErrorMessage(err, "Erro"));
    } finally {
      setLoading(false);
    }
  };

  // ── Resend Button Component ──
  const ResendButton = ({ purpose }: { purpose: "login" | "register" | "reset_password" }) => (
    <div className="text-center mt-4">
      <p className="text-xs text-gray-500 mb-1">Não recebeu o código?</p>
      {resendCountdown > 0 ? (
        <span className="text-xs text-gray-500">
          Reenviar em <span className="text-emerald-400 font-mono">{resendCountdown}s</span>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => handleResendOtp(purpose)}
          disabled={loading}
          className="flex items-center gap-1.5 mx-auto text-sm text-emerald-400 hover:text-emerald-300 transition-colors font-medium"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Reenviar código
        </button>
      )}
    </div>
  );

  // ── OTP Input JSX (inline to avoid re-mount on re-render) ──
  const otpInputs = (
    <div className="flex gap-2 justify-center" onPaste={handleOtpPaste}>
      {otpDigits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => { otpRefs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={digit}
          onChange={(e) => handleOtpChange(i, e.target.value)}
          onKeyDown={(e) => handleOtpKeyDown(i, e)}
          onPaste={handleOtpPaste}
          onFocus={(e) => e.target.select()}
          className="w-12 h-14 text-center text-xl font-bold bg-[#0a0e1a]/60 border border-gray-600/50 rounded-lg text-emerald-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 outline-none transition-all"
        />
      ))}
    </div>
  );

  // ── Render Views ──
  const renderView = () => {
    switch (view) {
      case "login":
        return (
          <>
            <h2 className="text-lg font-semibold text-white mb-1">Entrar na sua conta</h2>
            <p className="text-sm text-gray-400 mb-6">Acesse seu dashboard de tráfego</p>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-300 text-sm">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input id="email" type="email" placeholder="seu@email.com" value={email}
                    onChange={(e) => setEmail(e.target.value)} required
                    className="bg-[#0a0e1a]/60 border-gray-600/50 text-white placeholder:text-gray-500 focus:border-emerald-500/50 focus:ring-emerald-500/20 h-11 pl-10" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-gray-300 text-sm">Senha</Label>
                  <button type="button" onClick={() => { resetForm(); setView("forgot-password"); }}
                    className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors">
                    Esqueci a senha
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input id="password" type={showPassword ? "text" : "password"} placeholder="••••••••"
                    value={password} onChange={(e) => setPassword(e.target.value)} required
                    className="bg-[#0a0e1a]/60 border-gray-600/50 text-white placeholder:text-gray-500 focus:border-emerald-500/50 focus:ring-emerald-500/20 h-11 pl-10 pr-10" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {error && <ErrorBox message={error} />}
              <Button type="submit" disabled={loading}
                className="w-full h-11 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white font-medium">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                {loading ? "Entrando..." : "Entrar"}
              </Button>
            </form>
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-700/50" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-[#111827] px-3 text-gray-500">ou</span></div>
            </div>
            <button onClick={() => { resetForm(); setView("otp-login"); }}
              className="flex items-center justify-center gap-2 w-full h-11 rounded-lg border border-gray-600/50 text-gray-300 hover:bg-gray-700/30 hover:text-white transition-colors text-sm font-medium">
              <KeyRound className="w-4 h-4" />
              Entrar com código por email
            </button>
            <p className="text-center text-sm text-gray-400 mt-6">
              Não tem conta?{" "}
              <button onClick={() => { resetForm(); setView("register"); }}
                className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors">
                Criar conta
              </button>
            </p>
          </>
        );

      case "register":
        return (
          <>
            <button onClick={() => { resetForm(); setView("login"); }}
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Voltar
            </button>
            <h2 className="text-lg font-semibold text-white mb-1">Criar sua conta</h2>
            <p className="text-sm text-gray-400 mb-6">Comece a otimizar seu tráfego com IA</p>
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-300 text-sm">Nome</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input type="text" placeholder="Seu nome" value={name}
                    onChange={(e) => setName(e.target.value)} required
                    className="bg-[#0a0e1a]/60 border-gray-600/50 text-white placeholder:text-gray-500 focus:border-emerald-500/50 focus:ring-emerald-500/20 h-11 pl-10" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300 text-sm">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input type="email" placeholder="seu@email.com" value={email}
                    onChange={(e) => setEmail(e.target.value)} required
                    className="bg-[#0a0e1a]/60 border-gray-600/50 text-white placeholder:text-gray-500 focus:border-emerald-500/50 focus:ring-emerald-500/20 h-11 pl-10" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300 text-sm">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input type={showPassword ? "text" : "password"} placeholder="Mínimo 6 caracteres"
                    value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                    className="bg-[#0a0e1a]/60 border-gray-600/50 text-white placeholder:text-gray-500 focus:border-emerald-500/50 focus:ring-emerald-500/20 h-11 pl-10 pr-10" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {error && <ErrorBox message={error} />}
              <Button type="submit" disabled={loading}
                className="w-full h-11 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white font-medium">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                {loading ? "Enviando código..." : "Criar conta"}
              </Button>
            </form>
          </>
        );

      case "register-verify":
        return (
          <>
            <button onClick={() => { resetForm(); setView("register"); }}
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Voltar
            </button>
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-7 h-7 text-emerald-400" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-1">Verifique seu email</h2>
              <p className="text-sm text-gray-400">Enviamos um código de 6 dígitos para <span className="text-emerald-400">{email}</span></p>
            </div>
            <form ref={formSubmitRef} onSubmit={handleRegisterVerify} className="space-y-5">
              {otpInputs}
              {error && <ErrorBox message={error} />}
              {success && <SuccessBox message={success} />}
              <Button type="submit" disabled={loading || otpCode.length < 6}
                className="w-full h-11 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white font-medium">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {loading ? "Verificando..." : "Confirmar e criar conta"}
              </Button>
            </form>
            <ResendButton purpose="register" />
          </>
        );

      case "otp-login":
        return (
          <>
            <button onClick={() => { resetForm(); setView("login"); }}
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Voltar
            </button>
            <h2 className="text-lg font-semibold text-white mb-1">Login com código</h2>
            <p className="text-sm text-gray-400 mb-6">Enviaremos um código de acesso para seu email</p>
            <form onSubmit={handleRequestOtp} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-300 text-sm">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input type="email" placeholder="seu@email.com" value={email}
                    onChange={(e) => setEmail(e.target.value)} required
                    className="bg-[#0a0e1a]/60 border-gray-600/50 text-white placeholder:text-gray-500 focus:border-emerald-500/50 focus:ring-emerald-500/20 h-11 pl-10" />
                </div>
              </div>
              {error && <ErrorBox message={error} />}
              <Button type="submit" disabled={loading}
                className="w-full h-11 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white font-medium">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                {loading ? "Enviando..." : "Enviar código"}
              </Button>
            </form>
          </>
        );

      case "otp-verify":
        return (
          <>
            <button onClick={() => { resetForm(); setView("otp-login"); }}
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Voltar
            </button>
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                <KeyRound className="w-7 h-7 text-emerald-400" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-1">Digite o código</h2>
              <p className="text-sm text-gray-400">Enviamos para <span className="text-emerald-400">{email}</span></p>
            </div>
            <form ref={formSubmitRef} onSubmit={handleVerifyOtp} className="space-y-5">
              {otpInputs}
              {error && <ErrorBox message={error} />}
              {success && <SuccessBox message={success} />}
              <Button type="submit" disabled={loading || otpCode.length < 6}
                className="w-full h-11 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white font-medium">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                {loading ? "Verificando..." : "Entrar"}
              </Button>
            </form>
            <ResendButton purpose="login" />
          </>
        );

      case "forgot-password":
        return (
          <>
            <button onClick={() => { resetForm(); setView("login"); }}
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Voltar
            </button>
            <h2 className="text-lg font-semibold text-white mb-1">Esqueceu a senha?</h2>
            <p className="text-sm text-gray-400 mb-6">Enviaremos um código para redefinir</p>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-300 text-sm">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input type="email" placeholder="seu@email.com" value={email}
                    onChange={(e) => setEmail(e.target.value)} required
                    className="bg-[#0a0e1a]/60 border-gray-600/50 text-white placeholder:text-gray-500 focus:border-emerald-500/50 focus:ring-emerald-500/20 h-11 pl-10" />
                </div>
              </div>
              {error && <ErrorBox message={error} />}
              <Button type="submit" disabled={loading}
                className="w-full h-11 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white font-medium">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                {loading ? "Enviando..." : "Enviar código"}
              </Button>
            </form>
          </>
        );

      case "reset-password":
        return (
          <>
            <button onClick={() => { resetForm(); setView("forgot-password"); }}
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Voltar
            </button>
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-7 h-7 text-emerald-400" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-1">Redefinir senha</h2>
              <p className="text-sm text-gray-400">Código enviado para <span className="text-emerald-400">{email}</span></p>
            </div>
            <form onSubmit={handleResetPassword} className="space-y-5">
              {otpInputs}
              <div className="space-y-2">
                <Label className="text-gray-300 text-sm">Nova senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input type="password" placeholder="Mínimo 6 caracteres" value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)} required minLength={6}
                    className="bg-[#0a0e1a]/60 border-gray-600/50 text-white placeholder:text-gray-500 focus:border-emerald-500/50 focus:ring-emerald-500/20 h-11 pl-10" />
                </div>
              </div>
              {error && <ErrorBox message={error} />}
              {success && <SuccessBox message={success} />}
              <Button type="submit" disabled={loading || otpCode.length < 6}
                className="w-full h-11 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white font-medium">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {loading ? "Redefinindo..." : "Redefinir senha"}
              </Button>
            </form>
            <ResendButton purpose="reset_password" />
          </>
        );

      case "reset-success":
        return (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-lg font-semibold text-white mb-2">Senha redefinida!</h2>
            <p className="text-sm text-gray-400 mb-6">Sua nova senha foi configurada com sucesso.</p>
            <Button onClick={() => { resetForm(); setView("login"); }}
              className="w-full h-11 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white font-medium">
              <ArrowRight className="w-4 h-4 mr-2" />
              Fazer login
            </Button>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center p-4">
      {/* Background effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/assets/logo-manyflows.png"
            alt="Manyflows ADS AI"
            className="h-12 mb-4 mx-auto"
          />
          <p className="text-sm text-gray-400 mt-1">DASH INTELLIGENCE ADS AI</p>
        </div>

        {/* Auth Card */}
        <div className="bg-[#111827]/80 backdrop-blur-xl border border-gray-700/50 rounded-2xl p-8 shadow-2xl shadow-black/30">
          {renderView()}
        </div>

        <p className="text-center text-xs text-gray-600 mt-6">
          Protegido por criptografia de ponta a ponta
        </p>
      </div>
    </div>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-400">
      {message}
    </div>
  );
}

function SuccessBox({ message }: { message: string }) {
  return (
    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-sm text-emerald-400">
      {message}
    </div>
  );
}
